import { reactive, provide, inject, type InjectionKey } from 'vue';

/**
 * PreSet 패널 공통 진행 상태(중앙 오버레이용).
 * - 호스트(PreSetDocGenView)가 `providePresetProgress()` 로 reactive 상태를 제공하고
 *   오버레이를 한 곳에서 렌더한다.
 * - 각 패널은 `usePresetProgress()` 로 주입받아 자기 creating/progress 를 흘려보내기만 한다.
 *   (패널은 버튼 :loading 등 로컬 상태를 그대로 유지)
 */
export interface PresetProgressState {
  busy: boolean;
  message: string;
}

export const PRESET_PROGRESS_KEY: InjectionKey<PresetProgressState> = Symbol('presetProgress');

/** 호스트에서 호출 — reactive 상태를 만들어 provide 하고 그 객체를 반환(오버레이 바인딩용). */
export function providePresetProgress(): PresetProgressState {
  const state = reactive<PresetProgressState>({ busy: false, message: '' });
  provide(PRESET_PROGRESS_KEY, state);
  return state;
}

/**
 * 패널에서 호출 — 주입된 진행 상태를 반환. 호스트 밖에서 단독 사용 시 no-op 안전 객체.
 * 사용 예:
 *   const presetProgress = usePresetProgress();
 *   watch(() => creating.value, v => { presetProgress.busy = v; });
 *   watch(() => progress.value, v => { presetProgress.message = v; });
 */
export function usePresetProgress(): PresetProgressState {
  return inject(PRESET_PROGRESS_KEY, reactive<PresetProgressState>({ busy: false, message: '' }));
}
